"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Sparkles, Heart, TrendingUp, BookOpen, CheckCircle2 } from "lucide-react"
import { getDefaultReviewYear } from "../utils"

interface LandingIntroProps {
  onBegin: () => void
}

export function LandingIntro({ onBegin }: LandingIntroProps) {
  const currentYear = getDefaultReviewYear()

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        {/* <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Sparkles className="h-8 w-8 text-primary" /> 
        </div> */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm mb-4">
          <Sparkles className="h-4 w-4" />A Gentle Year-End Reflection
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          Your {currentYear} Year in Review
        </h1>
        <h1 className="text-2xl md:text-2xl font-bold text-balance leading-tight">
          Before You Plan the Next Year, <span className="text-primary">Honor This One</span>
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          A thoughtful reflection to help you process, celebrate, and learn from your year — feel proud and see how you grew.
        </p>
       
        {/* <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto text-pretty">
          This isn't about productivity or achievements. It's about understanding yourself more deeply — your
          growth, your needs, your journey.
        </p> */}
        <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          Take 15-20 minutes. Answer what feels true. Skip what doesn't resonate. 
          {/* <br/>There are no wrong answers here. */}
          <br/> You'll get a link to your review at the end of the journey.
        </p>
      </div>

      {/* What You'll Do */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">What You'll Explore</CardTitle>
          <CardDescription>
            This isn't a checklist or achievement tracker. It's a space to reflect on your year with depth and intention.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Heart className="h-3 w-3 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Claim Your Wins</h3>
                <p className="text-sm text-muted-foreground">
                  Recognize quiet victories, progress made, and moments you chose yourself
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <TrendingUp className="h-3 w-3 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Reflect on Your Journey</h3>
                <p className="text-sm text-muted-foreground">
                  Explore turning points, what changed, and how you grew through challenges
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <BookOpen className="h-3 w-3 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Understand Your Patterns</h3>
                <p className="text-sm text-muted-foreground">
                  Notice what gave you energy, what drained you, and how you navigated relationships
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="mt-1 h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-3 w-3 text-primary" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Close the Year Mindfully</h3>
                <p className="text-sm text-muted-foreground">
                  Let go of what no longer serves you and set intentions for what's ahead
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
      <CardHeader>
          <CardTitle className="text-2xl">Why it's worth your time</CardTitle>
            <CardDescription>
              Give yourself a stable ground, you don't have to start from 0 this year. 
              <br/>See clearly where to move forward and how to overcome what holds you back.
              <br/>Notice what gives you strength and motivation.          
            </CardDescription>
        </CardHeader>
      </Card>

      {/* Science-Backed */}
      <Card className="bg-muted/50">
        <CardContent className="pt-2">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <h3 className="font-medium mb-1">Science-Backed Reflection</h3>
              <p className="text-sm text-muted-foreground">
                Each section includes insights from psychology and neuroscience research, helping you understand why these questions matter and how reflection supports growth.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Auto-Save Note */}
      <Card className="border-primary/20">
        <CardContent className="pt-2">
          <p className="text-sm text-center text-muted-foreground">
            Your responses are private and saved securely while you're typing. 
            <br/>We'll only ask for your email to save your progress. 
            <br/> Reach your review anytime using the link you'll get at the end of this insightful journey ✨
          </p>
        </CardContent>
      </Card>

      {/* CTA Button */}
      <div className="flex justify-center pt-4">
        <Button 
          size="lg" 
          className="text-base px-8"
          onClick={onBegin}
        >
          Begin Your Reflection
        </Button>
      </div>
    </div>
  )
}

